import { ILoadFileRequestParams } from '../../file-download';
import { HttpStatusCode, RestClientHttpError } from '../../restClient';
import { storageSessionCredentials } from '../../storage';
import { remoteStorageConfiguration } from '../configuration';
import { getSignedFileRequestData } from '../utils';
import { loadRemoteStorageCredentials } from './loadRemoteStorageCredentials';

type ILoadRemoteEntityRequestParams<P> = ILoadFileRequestParams & P;

type TLoadRemoteEntityRequestFn<T, P> = (
  requestUrl: string,
  params?: ILoadRemoteEntityRequestParams<P>
) => T;

export async function loadRemoteEntity<T, P extends Record<string, any> = never>(
  url: string,
  params: ILoadRemoteEntityRequestParams<P>,
  requestFn: TLoadRemoteEntityRequestFn<T, P>,
  retries = 0
): Promise<T> {
  const credentials = storageSessionCredentials.get();

  try {
    let requestUrl = url;
    let requestHeaders;

    if (credentials) {
      const signedObjectUrl = await getSignedFileRequestData(url, credentials);
      requestUrl = signedObjectUrl?.url || url;
      requestHeaders = signedObjectUrl?.headers || {};
    }

    const newParams = {
      ...params,
      headers: requestHeaders,
    };

    const result = await requestFn(requestUrl, newParams);
    return result;
  } catch (error) {
    const restError = error as RestClientHttpError;
    const isCredentialsError =
      !!remoteStorageConfiguration.getCredentialsRequest &&
      (restError.statusCode === HttpStatusCode.Forbidden ||
        restError.statusCode === HttpStatusCode.Unauthorized);

    if (isCredentialsError && retries < remoteStorageConfiguration.fileRequestRetriesCount) {
      await loadRemoteStorageCredentials();
      return loadRemoteEntity(url, params, requestFn, retries + 1);
    }

    throw error;
  }
}
